import React from "react";

import { withTranslation } from "next-i18next";

import { Button, Section, Text } from "components/ui/source";
import { LoadingLayout } from "components/ui/source/layouts";

import Form from "../atoms/Forms/Form";
import Modal from "../atoms/Modal";
import Spacer from "../layouts/Spacer";
import ConfirmationCodeSignInApi from "../shared/api/ConfirmationCodeSignInApi";

import ConfirmationCodeEntryForm from "./ConfirmationCodeEntryForm";
import ConfirmationCodeStartVerificationFlow from "./ConfirmationCodeStartVerificationFlow";
import ConfirmationCodeVerificationForm from "./ConfirmationCodeVerificationForm";

// Don't know why, but importing Form, Modal from components/ui/source
// causes invalid hook error

import styles from "../components/Accounts/Accounts.module.scss";

/**
 * ConfirmationCodeSignInPopup component is used to support user authentication via the confirmation code
 * and the verification code sent to the user's phone number. It is currently used by Airbnb guests
 * in order to complete their online check-in process.
 *
 * A typical flow of the component is as follows:
 *   1. User enters the confirmation code received via email
 *   2. If the confirmation code is valid, user will receive a verification code via SMS
 *   3. User enters the verification code
 *   4. If the verification code is valid, user will be authenticated and redirected to the next step
 *
 */
class ConfirmationCodeSignInPopup extends React.Component {
  constructor(props) {
    super(props);
    const confirmationCode =
      this.props.confirmationCodePopupProps?.confirmationCode;
    this.state = {
      step: "enterConfirmationCode",
      confirmationCode: confirmationCode,
      verificationCode: null,
      error: null,
      phoneNumber: null,
      submitLoading: false,
      reservationCity: null,
      verificationLink: null,
      skipAlertView: confirmationCode != null,
    };
  }

  componentDidMount() {
    this.startConfirmationCodeSignIn();
  }

  // renderEnterConfirmationCode component
  startConfirmationCodeSignIn = () => {
    if (!this.state.confirmationCode) {
      return;
    }
    this.setState({ submitLoading: true });
    ConfirmationCodeSignInApi.startConfirmationCodeSignIn(
      this.state.confirmationCode,
    ).then(response => {
      this.setState({ submitLoading: false });
      if (response.error) {
        this.setState({ error: response.error });
      } else if (response.signed_in) {
        return window.location.assign(response.verification_link);
      } else if (response.verification_link) {
        if (this.state.skipAlertView) {
          this.setState({
            submitLoading: true,
            verificationLink: response.verification_link,
            error: null,
          });
          this.startVerificationFlow();
        } else {
          this.setState({
            step: "startVerificationFlow",
            reservationCity: response.city,
            verificationLink: response.verification_link,
            error: null,
          });
        }
      } else if (!response.has_account) {
        if (this.state.skipAlertView) {
          this.setState({ submitLoading: true, error: null });
          this.props.openSignUpPopup();
        } else {
          this.setState({ step: "needToCreateAccount", error: null });
        }
      } else {
        this.setState({
          step: "enterVerificationCode",
          phoneNumber: response.phone_number,
          error: null,
        });
      }
    });
  };

  confirmationChanged = confirmationCode => {
    this.setState({ confirmationCode });
    return true;
  };

  // renderStartVerificationFlow component
  startVerificationFlow() {
    return window.location.assign(this.state.verificationLink);
  }

  // renderEnterVerificationCode component
  confirmVerificationCode = () => {
    const confirmationCode =
      this.props.confirmationCodePopupProps?.confirmationCode ??
      this.state.confirmationCode;
    this.setState({ submitLoading: true });
    ConfirmationCodeSignInApi.confirmPhoneVerificationCode(
      confirmationCode,
      this.state.verificationCode,
    ).then(response => {
      this.setState({ submitLoading: false });

      if (response.error) {
        return this.setState({ error: response.error });
      }

      if (response.redirect_path) {
        return window.location.assign(response.redirect_path);
      }

      this.setState({ step: "complete", error: null });
      return window.location.assign(response.redirect_path);
    });
  };

  verificationEntered = code => {
    this.setState({
      verificationCode: code,
    });
    return true;
  };

  resendVerificationCode = () => {
    const confirmationCode =
      this.props.confirmationCodePopupProps?.confirmationCode ??
      this.state.confirmationCode;
    this.setState({ submitLoading: true });
    ConfirmationCodeSignInApi.startConfirmationCodeSignIn(
      confirmationCode,
    ).then(response => {
      this.setState({ submitLoading: false });

      if (response.error) {
        return this.setState({ error: response.error });
      }
    });
  };

  resendVerificationCodePhone = () => {
    const confirmationCode =
      this.props.confirmationCodePopupProps?.confirmationCode ??
      this.state.confirmationCode;
    this.setState({ submitLoading: true });
    ConfirmationCodeSignInApi.startConfirmationCodeSignIn(
      confirmationCode,
      "call",
    ).then(response => {
      this.setState({ submitLoading: false });

      if (response.error) {
        return this.setState({ error: response.error });
      }
    });
  };

  // Components rendering
  renderEnterConfirmationCode() {
    return (
      <ConfirmationCodeEntryForm
        error={this.state.error}
        onSubmit={this.startConfirmationCodeSignIn.bind(this)}
        onChange={this.confirmationChanged.bind(this)}
        onSignUp={this.props.openSignUpPopup}
        disableButton={!this.state.confirmationCode}
      />
    );
  }

  renderStartVerificationFlow() {
    return (
      <ConfirmationCodeStartVerificationFlow
        city={this.state.reservationCity}
        onSubmit={this.startVerificationFlow.bind(this)}
      />
    );
  }

  renderEnterVerificationCode() {
    const phoneNumber =
      this.props.confirmationCodePopupProps?.phoneNumber ??
      this.state.phoneNumber;
    return (
      <ConfirmationCodeVerificationForm
        error={this.state.error}
        phoneNumber={phoneNumber}
        onSubmit={this.confirmVerificationCode.bind(this)}
        onCodeEntered={this.verificationEntered}
        onResendClick={this.resendVerificationCode}
        onAudioClick={this.resendVerificationCodePhone}
        disableButton={!this.state.verificationCode}
      />
    );
  }

  renderNeedToCreateAccount() {
    return (
      <>
        <Spacer size="s-2x" />
        <Section orientation="column">
          <Text
            translateKey="sonder_one.popups.ConfirmationCodeSignInPopup.headerNeedAccount"
            types={["subtitles"]}
          />
          <Button
            type="no_background"
            textProps={{
              translateKey:
                "sonder_one.popups.ConfirmationCodeSignInPopup.signUp",
              types: ["subtitles", "underline"],
            }}
            upperCase={false}
            onClick={this.props.openSignUpPopup}
          />
        </Section>
      </>
    );
  }

  renderComplete() {
    return (
      <>
        <Spacer size="s-2x" />
        <Section>
          <Text
            translateKey="sonder_one.popups.ConfirmationCodeSignInPopup.headerComplete"
            types={["subtitles"]}
          />
        </Section>
      </>
    );
  }

  handleHide() {
    if (this.state.step === "complete") {
      window.location.reload();
    }
    this.props.closeConfirmationPopup();
  }

  modalProps() {
    return {
      isOpen: !this.props.hidden,
      onRequestClose: this.handleHide.bind(this),
      shouldCloseOnOverlayClick: true,
      shouldCloseOnEsc: true,
      className: styles.modal,
    };
  }

  render() {
    const step = this.props.confirmationCodePopupProps?.step ?? this.state.step;
    return (
      <Modal modalProps={this.modalProps()}>
        <LoadingLayout loading={this.state.submitLoading}>
          <Modal.Close onClick={this.handleHide.bind(this)} />
          <Form
            initialValues={this.state}
            enableReinitialize={true}
            onSubmit={() => {}}
          >
            {({}) => (
              <>
                {step === "enterConfirmationCode" && (
                  <div>{this.renderEnterConfirmationCode()}</div>
                )}
                {step === "startVerificationFlow" && (
                  <div>{this.renderStartVerificationFlow()}</div>
                )}
                {step === "enterVerificationCode" && (
                  <div>{this.renderEnterVerificationCode()}</div>
                )}
                {step === "needToCreateAccount" && (
                  <div>{this.renderNeedToCreateAccount()}</div>
                )}
                {step === "complete" && <div>{this.renderComplete()}</div>}
              </>
            )}
          </Form>
        </LoadingLayout>
      </Modal>
    );
  }
}

export default withTranslation()(ConfirmationCodeSignInPopup);
